import { useState } from "react";
import axios from "axios";
import { type UserStory } from "@/lib/projectStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Wand2,
  Loader2,
  Trash2,
  Plus,
  X,
  CheckCircle2,
  AlertTriangle,
  Bot,
  BookOpen,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

type Step = "review" | "generating" | "edit" | "confirm";

interface DraftStory {
  _key: string; // local UI key only
  title: string;
  asA: string;
  iWant: string;
  soThat: string;
  acceptanceCriteria: string[];
  priority: "must" | "should" | "could" | "wont";
  storyPoints: number;
  included: boolean;
}

interface Props {
  requirement: any;
  onConfirm: (stories: UserStory[]) => void;
  onClose: () => void;
}

const priorityColors: Record<string, string> = {
  must: "bg-destructive/15 text-destructive border-destructive/30",
  should: "bg-accent/15 text-accent border-accent/30",
  could: "bg-secondary text-secondary-foreground border-border",
  wont: "bg-muted text-muted-foreground border-border",
};

const priorityLabels: Record<string, string> = {
  must: "Must Have",
  should: "Should Have",
  could: "Could Have",
  wont: "Won't Have",
};

export default function RequirementToStoryWizard({ requirement, onConfirm, onClose }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [step, setStep] = useState<Step>("review");
  const [drafts, setDrafts] = useState<DraftStory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ragContextUsed, setRagContextUsed] = useState(false);
  const [ragChunks, setRagChunks] = useState(0);

  // ─── Step 1: Review ────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    setStep("generating");
    setError(null);
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/requirement-to-story`,
        { requirement_id: requirement.id },
        { headers: authHeader }
      );
      const raw: any[] = data.stories || [];
      setDrafts(
        raw.map((s, i) => ({
          _key: `draft-${i}-${Date.now()}`,
          title: s.title || "",
          asA: s.asA || "",
          iWant: s.iWant || "",
          soThat: s.soThat || "",
          acceptanceCriteria: Array.isArray(s.acceptanceCriteria) ? s.acceptanceCriteria : [],
          priority: (["must", "should", "could", "wont"].includes(s.priority) ? s.priority : "should") as DraftStory["priority"],
          storyPoints: typeof s.storyPoints === "number" ? s.storyPoints : 3,
          included: true,
        }))
      );
      setRagContextUsed(data.rag_context_used || false);
      setRagChunks(data.rag_chunks || 0);
      setStep("edit");
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Generation failed");
      setStep("review");
    }
  };

  // ─── Step 3: Edit helpers ───────────────────────────────────────────────────

  const updateDraft = (key: string, changes: Partial<DraftStory>) => {
    setDrafts((prev) => prev.map((d) => (d._key === key ? { ...d, ...changes } : d)));
  };

  const removeDraft = (key: string) => {
    setDrafts((prev) => prev.filter((d) => d._key !== key));
  };

  const addCriterion = (key: string) => {
    setDrafts((prev) =>
      prev.map((d) =>
        d._key === key
          ? { ...d, acceptanceCriteria: [...d.acceptanceCriteria, ""] }
          : d
      )
    );
  };

  const updateCriterion = (key: string, index: number, value: string) => {
    setDrafts((prev) =>
      prev.map((d) =>
        d._key === key
          ? {
              ...d,
              acceptanceCriteria: d.acceptanceCriteria.map((c, i) =>
                i === index ? value : c
              ),
            }
          : d
      )
    );
  };

  const removeCriterion = (key: string, index: number) => {
    setDrafts((prev) =>
      prev.map((d) =>
        d._key === key
          ? { ...d, acceptanceCriteria: d.acceptanceCriteria.filter((_, i) => i !== index) }
          : d
      )
    );
  };

  const addBlankStory = () => {
    setDrafts((prev) => [
      ...prev,
      {
        _key: `manual-${Date.now()}`,
        title: "",
        asA: "",
        iWant: "",
        soThat: "",
        acceptanceCriteria: [""],
        priority: "should",
        storyPoints: 3,
        included: true,
      },
    ]);
  };

  // ─── Step 4: Confirm ────────────────────────────────────────────────────────

  const handleConfirm = () => {
    const toAdd: UserStory[] = drafts
      .filter((d) => d.included && d.title.trim())
      .map((d) => ({
        id: crypto.randomUUID(),
        title: d.title.trim(),
        asA: d.asA.trim(),
        iWant: d.iWant.trim(),
        soThat: d.soThat.trim(),
        acceptanceCriteria: d.acceptanceCriteria.map((c) => c.trim()).filter(Boolean),
        priority: d.priority,
        storyPoints: d.storyPoints,
        status: "backlog" as const,
      }));
    onConfirm(toAdd);
  };

  const includedCount = drafts.filter((d) => d.included && d.title.trim()).length;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Wizard header */}
      <div className="border-b border-border px-4 py-3 flex items-center gap-3 shrink-0">
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className={step === "review" ? "text-foreground font-semibold" : ""}>
            1. Review
          </span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === "edit" ? "text-foreground font-semibold" : ""}>
            2. Edit Drafts
          </span>
          <ChevronRight className="h-3 w-3" />
          <span className={step === "confirm" ? "text-foreground font-semibold" : ""}>
            3. Confirm
          </span>
        </div>
        <span className="ml-auto text-[10px] font-mono text-muted-foreground/60 hidden sm:block">
          {requirement.title}
        </span>
      </div>

      <div className="flex-1 overflow-auto">

        {/* ── STEP 1: Review requirement ─────────────────────────────────────── */}
        {(step === "review" || step === "generating") && (
          <div className="p-6 max-w-2xl mx-auto space-y-5">
            <div>
              <h2 className="text-base font-semibold font-display text-foreground">
                Review Requirement
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Confirm the details below are accurate before the agent drafts stories. You will review and edit every draft before anything saves.
              </p>
            </div>

            {/* Requirement detail card */}
            <div className="border border-border rounded-lg bg-card divide-y divide-border">
              <div className="px-4 py-3 flex items-start justify-between gap-3">
                <span className="text-sm font-semibold font-body text-card-foreground">
                  {requirement.title}
                </span>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  <Badge variant="outline" className="text-[10px] px-1.5">{requirement.req_type}</Badge>
                  <Badge variant="outline" className="text-[10px] px-1.5">{requirement.status}</Badge>
                  {requirement.contains_phi && (
                    <Badge variant="outline" className="text-[10px] px-1.5 bg-destructive/10 text-destructive border-destructive/30 gap-0.5">
                      <AlertTriangle className="h-2.5 w-2.5" /> PHI
                    </Badge>
                  )}
                </div>
              </div>

              {requirement.raw_text && (
                <div className="px-4 py-3">
                  <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Raw Notes</p>
                  <p className="text-xs text-foreground/80 whitespace-pre-wrap">{requirement.raw_text}</p>
                </div>
              )}

              <div className="px-4 py-3 grid grid-cols-2 gap-x-6 gap-y-2">
                {[
                  ["Feature", requirement.feature],
                  ["Channel", requirement.channel],
                  ["Market", requirement.market],
                  ["Type", requirement.req_type],
                ].map(([label, value]) =>
                  value ? (
                    <div key={label}>
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{label}</p>
                      <p className="text-xs text-foreground">{value}</p>
                    </div>
                  ) : null
                )}
              </div>

              {(requirement.sensitivity_flags?.length > 0 || requirement.assumptions?.length > 0 || requirement.constraints_list?.length > 0) && (
                <div className="px-4 py-3 space-y-2">
                  {requirement.sensitivity_flags?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Sensitivity Flags</p>
                      <div className="flex flex-wrap gap-1">
                        {requirement.sensitivity_flags.map((f: string) => (
                          <Badge key={f} variant="outline" className="text-[10px] px-1.5 bg-amber-50 text-amber-700 border-amber-200">{f}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {requirement.assumptions?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Assumptions</p>
                      {requirement.assumptions.map((a: string, i: number) => (
                        <p key={i} className="text-xs text-foreground/80">· {a}</p>
                      ))}
                    </div>
                  )}
                  {requirement.constraints_list?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Constraints</p>
                      {requirement.constraints_list.map((c: string, i: number) => (
                        <p key={i} className="text-xs text-foreground/80">· {c}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && (
              <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between pt-1">
              <p className="text-xs text-muted-foreground max-w-sm">
                Mixtral will read this requirement and your project knowledge base to draft user stories. You review everything before it touches your backlog.
              </p>
              <Button
                onClick={handleGenerate}
                disabled={step === "generating"}
                className="gap-2 shrink-0"
              >
                {step === "generating" ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Drafting stories…</>
                ) : (
                  <><Wand2 className="h-3.5 w-3.5" /> Generate Story Drafts</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Edit drafts ────────────────────────────────────────────── */}
        {step === "edit" && (
          <div className="p-6 max-w-3xl mx-auto space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold font-display text-foreground">
                  Edit Story Drafts
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  These are AI suggestions — edit freely. Change any field, delete what doesn't fit, or add your own stories. Nothing saves until you confirm.
                </p>
              </div>
              {ragContextUsed && (
                <div className="flex items-center gap-1 text-[11px] text-accent shrink-0 border border-accent/30 rounded px-2 py-1 bg-accent/5">
                  <BookOpen className="h-3 w-3" />
                  {ragChunks} policy chunk{ragChunks !== 1 ? "s" : ""} used
                </div>
              )}
            </div>

            <div className="space-y-4">
              {drafts.map((draft, idx) => (
                <div
                  key={draft._key}
                  className={`border rounded-lg bg-card transition-opacity ${!draft.included ? "opacity-40" : "border-border"}`}
                >
                  {/* Story card header */}
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-secondary/30 rounded-t-lg">
                    <span className="text-[11px] font-mono text-muted-foreground">
                      Story {idx + 1}
                    </span>
                    <div className="flex-1" />
                    <button
                      onClick={() => updateDraft(draft._key, { included: !draft.included })}
                      className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${
                        draft.included
                          ? "border-primary/30 text-primary bg-primary/5 hover:bg-destructive/5 hover:text-destructive hover:border-destructive/30"
                          : "border-border text-muted-foreground hover:text-primary hover:border-primary/30"
                      }`}
                    >
                      {draft.included ? "included" : "excluded"}
                    </button>
                    <button
                      onClick={() => removeDraft(draft._key)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      title="Remove story"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="p-4 space-y-3">
                    {/* Title */}
                    <Input
                      value={draft.title}
                      onChange={(e) => updateDraft(draft._key, { title: e.target.value })}
                      placeholder="Story title"
                      className="font-semibold"
                    />

                    {/* As a / I want / So that */}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">As a…</p>
                        <Input
                          value={draft.asA}
                          onChange={(e) => updateDraft(draft._key, { asA: e.target.value })}
                          placeholder="role"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">I want…</p>
                        <Input
                          value={draft.iWant}
                          onChange={(e) => updateDraft(draft._key, { iWant: e.target.value })}
                          placeholder="capability"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">So that…</p>
                        <Input
                          value={draft.soThat}
                          onChange={(e) => updateDraft(draft._key, { soThat: e.target.value })}
                          placeholder="outcome"
                        />
                      </div>
                    </div>

                    {/* Acceptance criteria */}
                    <div>
                      <p className="text-[10px] font-mono text-muted-foreground mb-1.5">
                        Acceptance Criteria
                      </p>
                      <div className="space-y-1.5">
                        {draft.acceptanceCriteria.map((c, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <span className="text-[10px] font-mono text-muted-foreground/60 w-4 text-right shrink-0">
                              {i + 1}.
                            </span>
                            <Input
                              value={c}
                              onChange={(e) => updateCriterion(draft._key, i, e.target.value)}
                              placeholder="Given / When / Then…"
                              className="text-xs h-7"
                            />
                            <button
                              onClick={() => removeCriterion(draft._key, i)}
                              className="text-muted-foreground hover:text-destructive shrink-0"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addCriterion(draft._key)}
                          className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors mt-1"
                        >
                          <Plus className="h-3 w-3" /> Add criterion
                        </button>
                      </div>
                    </div>

                    {/* Priority + Points */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">Priority</p>
                        <Select
                          value={draft.priority}
                          onValueChange={(v) => updateDraft(draft._key, { priority: v as any })}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="must">Must Have</SelectItem>
                            <SelectItem value="should">Should Have</SelectItem>
                            <SelectItem value="could">Could Have</SelectItem>
                            <SelectItem value="wont">Won't Have</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-24">
                        <p className="text-[10px] font-mono text-muted-foreground mb-1">Story Points</p>
                        <Input
                          type="number"
                          min={1}
                          max={21}
                          value={draft.storyPoints}
                          onChange={(e) => updateDraft(draft._key, { storyPoints: +e.target.value })}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="pt-4">
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 ${priorityColors[draft.priority]}`}
                        >
                          {priorityLabels[draft.priority]}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add blank story */}
            <button
              onClick={addBlankStory}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-lg px-4 py-3 w-full transition-colors hover:border-foreground/30"
            >
              <Plus className="h-3.5 w-3.5" /> Add a story manually
            </button>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-muted-foreground">
                {includedCount} of {drafts.length} stories included
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep("review")}>
                  Back
                </Button>
                <Button
                  size="sm"
                  onClick={() => setStep("confirm")}
                  disabled={includedCount === 0}
                  className="gap-1.5"
                >
                  Review & Confirm <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Confirm ────────────────────────────────────────────────── */}
        {step === "confirm" && (
          <div className="p-6 max-w-2xl mx-auto space-y-5">
            <div>
              <h2 className="text-base font-semibold font-display text-foreground">
                Confirm & Add to Backlog
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                These {includedCount} stories will be added to your project backlog as draft items. You can edit them any time in the User Stories tab.
              </p>
            </div>

            <div className="space-y-3">
              {drafts
                .filter((d) => d.included && d.title.trim())
                .map((d, i) => (
                  <div key={d._key} className="border border-border rounded-lg bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold font-body text-card-foreground">
                        {d.title}
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-1.5 ml-auto shrink-0 ${priorityColors[d.priority]}`}
                      >
                        {priorityLabels[d.priority]}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 shrink-0">
                        {d.storyPoints} pts
                      </Badge>
                    </div>
                    {d.asA && (
                      <p className="text-xs text-muted-foreground">
                        As a <span className="font-semibold text-foreground">{d.asA}</span>, I want{" "}
                        <span className="font-semibold text-foreground">{d.iWant}</span>, so that{" "}
                        <span className="font-semibold text-foreground">{d.soThat}</span>
                      </p>
                    )}
                    {d.acceptanceCriteria.filter(Boolean).length > 0 && (
                      <div className="space-y-0.5 pt-1">
                        {d.acceptanceCriteria.filter(Boolean).map((c, ci) => (
                          <p key={ci} className="text-[11px] text-muted-foreground font-mono">✓ {c}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>

            <div className="border border-border rounded-lg bg-secondary/20 px-4 py-3 text-xs text-muted-foreground space-y-0.5">
              <p className="font-semibold text-foreground">What happens next</p>
              <p>· Stories are added to the backlog with status <span className="font-mono">Backlog</span>.</p>
              <p>· They appear immediately in User Stories, Board, and Timeline tabs.</p>
              <p>· You can edit, reprioritize, or delete them at any time.</p>
              <p>· This action is logged in the Agent Log tab.</p>
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button variant="outline" size="sm" onClick={() => setStep("edit")}>
                Back to Edit
              </Button>
              <Button onClick={handleConfirm} className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Add {includedCount} {includedCount === 1 ? "story" : "stories"} to Backlog
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
